import React, { forwardRef, useCallback } from 'react';
import { Field, mergeClasses } from '@fluentui/react-components';
import { TimePicker as FluentTimePicker } from '@fluentui/react-timepicker-compat';
import type {
  TimePickerProps as FluentTimePickerProps,
  TimeSelectionEvents,
  TimeSelectionData,
} from '@fluentui/react-timepicker-compat';
import { useTimePickerStyles } from './TimePicker.styles';

/** Convert a "HH:mm" string to a Date (today's date with given time). */
export function timeStringToDate(time: string): Date | null {
  if (!time) return null;
  const parts = time.split(':').map(Number);
  const hours = parts[0];
  const minutes = parts[1];
  if (hours === undefined || minutes === undefined || isNaN(hours) || isNaN(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/** Convert a Date to a "HH:mm" string. */
export function dateToTimeString(date: Date | null | undefined): string {
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Convert a Date to a localized 12-hour "h:mm AM/PM" display string.
 * Uses a plain ASCII space (deterministic across runtimes) rather than
 * `toLocaleTimeString`, which can emit a narrow no-break space before AM/PM.
 */
export function formatDateTo12h(date: Date | null | undefined): string {
  if (!date) return '';
  const rawHours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const meridiem = rawHours >= 12 ? 'PM' : 'AM';
  const hours12 = rawHours % 12 === 0 ? 12 : rawHours % 12;
  return `${hours12}:${minutes} ${meridiem}`;
}

/** Convert a stored "HH:mm" 24-hour string to its 12-hour "h:mm AM/PM" display form. */
export function formatStoredTimeForDisplay(value: string | null | undefined): string {
  return formatDateTo12h(timeStringToDate(value ?? ''));
}

function roundToNearest15(minutes: number): number {
  const rounded = Math.round(minutes / 15) * 15;
  return rounded >= 60 ? 0 : rounded;
}

/** Parse free-form user input ("9", "9:30", "0930", "2:30 PM") into a Date, snapping to 15 min. */
export const parseTimeStringToDate: NonNullable<FluentTimePickerProps['parseTimeStringToDate']> = (
  time
) => {
  if (!time || !time.trim()) return { date: null };
  const match = time
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2}):?(\d{0,2})\s*(am|pm)?$/);
  if (!match) return { date: null, errorType: 'invalid-input' };
  let hours = parseInt(match[1] ?? '0', 10);
  const rawMinutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3];
  if (meridiem) {
    // 12-hour input: hour must be 1–12; convert to 24-hour.
    if (hours < 1 || hours > 12) return { date: null, errorType: 'invalid-input' };
    if (meridiem === 'pm' && hours !== 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;
  }
  if (hours > 23 || rawMinutes > 59) return { date: null, errorType: 'invalid-input' };
  const minutes = roundToNearest15(rawMinutes);
  const date = new Date();
  date.setHours(minutes >= 60 ? hours + 1 : hours, minutes >= 60 ? 0 : minutes, 0, 0);
  if (date.getHours() > 23) return { date: null, errorType: 'invalid-input' };
  return { date };
};

/** Restrict typed input to digits, ":", meridiem letters (A/P/M) and space. */
function defaultKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
  const allowedKeys = [
    'Tab',
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Enter',
    'Escape',
    'Home',
    'End',
  ];
  if (allowedKeys.includes(e.key)) return;
  if (e.key === ':') return;
  if (e.key === ' ') return;
  if (/^[apm]$/i.test(e.key)) return;
  if (e.key >= '0' && e.key <= '9') return;
  e.preventDefault();
}

/**
 * Props for GEM TimePicker component.
 * Extends native Fluent UI TimePicker compat props.
 */
export interface TimePickerProps extends Omit<FluentTimePickerProps, 'value'> {
  /** Label text displayed above the timepicker */
  label?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Validation message displayed below the timepicker */
  validationMessage?: string;
  /** Validation state controlling message styling */
  validationState?: 'error' | 'warning' | 'success' | 'none';
  /** Current value as "HH:mm" string */
  value?: string;
  /** Called with the new "HH:mm" string when user selects/types a time */
  onTimeStringChange?: (value: string) => void;
}

/**
 * Themed TimePicker — thin wrapper around @fluentui/react-timepicker-compat TimePicker + Field.
 * Applies GEM defaults: appearance="outline", size="large", hourCycle="h12" (localized AM/PM
 * display; values are stored/emitted as "HH:mm" 24-hour), 15 min increments, freeform input,
 * constrained listbox height, and "HH:mm" string I/O.
 */
export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      label,
      required,
      validationMessage,
      validationState,
      value,
      onTimeStringChange,
      onTimeChange,
      appearance = 'outline',
      size = 'large',
      hourCycle = 'h12',
      increment = 15,
      freeform = true,
      inlinePopup = true,
      input,
      listbox,
      positioning = {
        position: 'below',
        align: 'start',
        flipBoundary: 'scrollParent',
        overflowBoundary: 'scrollParent',
      },
      className,
      ...rest
    },
    ref
  ) => {
    const styles = useTimePickerStyles();

    const handleTimeChange = useCallback(
      (e: TimeSelectionEvents, data: TimeSelectionData) => {
        if (onTimeStringChange) {
          onTimeStringChange(dateToTimeString(data.selectedTime));
        }
        if (onTimeChange) {
          onTimeChange(e, data);
        }
      },
      [onTimeChange, onTimeStringChange]
    );

    const inputProps = {
      onKeyDown: defaultKeyDown,
      ...input,
    };

    const listboxProps =
      listbox && typeof listbox === 'object' && !React.isValidElement(listbox)
        ? {
            ...(listbox as Record<string, unknown>),
            className: mergeClasses(styles.listbox, (listbox as { className?: string }).className),
          }
        : { className: styles.listbox };

    const picker = (
      <FluentTimePicker
        ref={ref}
        appearance={appearance}
        size={size}
        hourCycle={hourCycle}
        increment={increment}
        freeform={freeform}
        inlinePopup={inlinePopup}
        parseTimeStringToDate={parseTimeStringToDate}
        formatDateToTimeString={formatDateTo12h}
        selectedTime={timeStringToDate(value ?? '')}
        value={formatStoredTimeForDisplay(value)}
        input={inputProps}
        listbox={listboxProps}
        positioning={positioning}
        onTimeChange={handleTimeChange}
        {...rest}
      />
    );

    // When no label/validation is provided, return the picker bare so it can
    // be composed inside an external Field (e.g. for time ranges) without
    // adding an extra wrapper div that breaks parent flex layouts.
    if (!label && !validationMessage && !validationState) {
      return picker;
    }

    return (
      <Field
        className={mergeClasses(styles.root, className)}
        label={label}
        required={required}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        {picker}
      </Field>
    );
  }
);

TimePicker.displayName = 'TimePicker';
